import { Search as SearchIcon } from '@mui/icons-material'
import { Autocomplete, type AutocompleteRenderGetTagProps, type AutocompleteRenderInputParams, Chip, type FilterOptionsState, InputBase, Stack, Typography, createFilterOptions } from '@mui/material'
import { type HTMLAttributes, type PropsWithChildren, type SyntheticEvent, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ItemListSearchParams } from '../../Common/ItemList/ItemList'
import ProductNavPicture, { hasProductNavImageSet } from '../../Common/ProductNavPicture'
import { StyledBar } from '../../Common/ItemList/SearchBar'

export interface ProductSearchFilterOption {
  id: number | string
  label: string
  imageSlug?: string
  emphasized?: boolean
}

interface ProductSearchValue extends ProductSearchFilterOption {
  type: 'filter' | 'query'
}

type SearchBarValue = ItemListSearchParams & {
  search: string[]
}

type Props<TValue extends SearchBarValue> = PropsWithChildren<{
  placeholder?: string
  value?: TValue
  onChange?: (value: (value: TValue) => TValue) => void
  filterField: Exclude<keyof TValue, 'search'> & string
  filterOptions: ProductSearchFilterOption[]
}>

const filter = createFilterOptions<ProductSearchValue>()

function SearchOptionAvatar(props: { slug: string, size: 32 | 36 }) {
  return <ProductNavPicture
    slug={props.slug}
    width={props.size}
    height={props.size}
    alt=""
    style={{
      height: props.size,
      width: 'auto',
      borderRadius: '50%',
      display: 'block',
    }}
  />
}

export default function BaseProductSearchBar<TValue extends SearchBarValue>(props: Props<TValue>) {
  const [t] = useTranslation('account-v2')

  const options = useMemo(() => {
    return props.filterOptions.map(option => ({
      ...option,
      type: 'filter' as const,
    }))
  }, [props.filterOptions])

  const onChange = useCallback((e: SyntheticEvent, value: Array<ProductSearchValue | string>) => {
    if (!props.onChange) return

    const filters: string[] = []
    const search: string[] = []

    value.forEach(option => {
      if (typeof option === 'string') {
        search.push(String(option))
        return
      }

      if (option.type === 'filter') {
        filters.push(String(option.id))
        return
      }

      search.push(String(option.label))
    })

    props.onChange(value => ({
      ...value,
      [props.filterField]: filters,
      search
    }))
  }, [props.filterField, props.onChange])

  const renderTags = useCallback((value: ProductSearchValue[], getTagProps: AutocompleteRenderGetTagProps) => {
    return value.map((option, index) => {
      const { key, ...tagProps } = getTagProps({ index })

      return <Chip
        key={key}
        label={option.label}
        avatar={option.imageSlug && hasProductNavImageSet(option.imageSlug) ? <SearchOptionAvatar
          slug={option.imageSlug}
          size={32}
        /> : undefined}
        sx={{
          fontWeight: option.emphasized ? 600 : 400
        }}
        {...tagProps}
      />
    })
  }, [])

  const renderInput = useCallback((params: AutocompleteRenderInputParams) => {
    return <div style={{
      display: 'flex',
      flexGrow: '1',
      height: '100%',
      minWidth: '0'
    }}>
      <InputBase
        placeholder={props.placeholder ?? t('Search...')}
        fullWidth
        sx={{
          alignSelf: 'stretch',
          display: 'flex',
          height: '100%',
          minWidth: '0',
          padding: '0',

          '& .MuiInputBase-input': {
            boxSizing: 'border-box',
            height: '100%',
            padding: '0',
          },

          '& .MuiAutocomplete-endAdornment': {
            right: '-6px',
          }
        }}
        inputProps={params.inputProps}
        {...params.InputProps}
      />
    </div>
  }, [props.placeholder, t])

  const renderOption = useCallback((props: HTMLAttributes<HTMLLIElement> & { key: string }, option: ProductSearchValue) => {
    const { key, ...otherProps } = props

    return <li
      key={key}
      style={{
        padding: 0
      }}
      {...otherProps}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{
          py: 0.5,
          px: 1
        }}
      >
        {option.imageSlug && hasProductNavImageSet(option.imageSlug) ? <SearchOptionAvatar
          slug={option.imageSlug}
          size={36}
        /> : null}
        <Typography
          variant="body1"
          sx={{
            fontWeight: option.emphasized ? 600 : 400,
            lineHeight: '36px'
          }}
        >{option.label}</Typography>
      </Stack>
    </li>
  }, [])

  const getOptionKey = useCallback((option: ProductSearchValue | string) => {
    if (typeof option === 'string') {
      return option
    }

    return option.type === 'filter'
      ? 'filter|' + option.id
      : 'query|' + option.label
  }, [])

  const getOptionLabel = useCallback((option: ProductSearchValue | string) => {
    return typeof option === 'string' ? option : option.label
  }, [])

  const filterOptions = useCallback((options: ProductSearchValue[], state: FilterOptionsState<ProductSearchValue>) => {
    const filtered = filter(options, state)
    const { inputValue } = state

    const isExisting = options.some(option => inputValue === option.label)
    if (inputValue !== '' && !isExisting) {
      filtered.push({
        id: 0,
        label: inputValue,
        type: 'query'
      })
    }

    return filtered
  }, [])

  const isOptionEqualToValue = useCallback((option: ProductSearchValue, value: ProductSearchValue) => {
    return option.type === value.type &&
      option.id === value.id &&
      option.label === value.label
  }, [])

  const value = useMemo(() => {
    if (!props.value) return []

    const valueArray: ProductSearchValue[] = []
    const selectedFilterValues = props.value[props.filterField] ?? []

    selectedFilterValues.forEach(optionId => {
      const option = options.find(option => String(option.id) === String(optionId))
      if (!option) return

      valueArray.push(option)
    })

    props.value.search.forEach(searchText => {
      valueArray.push({
        type: 'query',
        id: 0,
        label: searchText
      })
    })

    return valueArray
  }, [options, props.filterField, props.value])

  return <StyledBar>
    <SearchIcon sx={{ color: 'action.active', fontSize: '28px' }} />
    <Autocomplete
      multiple
      freeSolo
      autoHighlight
      clearOnBlur
      filterSelectedOptions
      options={options}
      value={value}
      onChange={onChange}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionKey={getOptionKey}
      getOptionLabel={getOptionLabel}
      renderTags={renderTags}
      renderInput={renderInput}
      renderOption={renderOption}
      filterOptions={filterOptions}
      sx={{
        alignSelf: 'stretch',
        display: 'flex',
        flexGrow: '1',
        minWidth: '0',
        '& .MuiAutocomplete-inputRoot': {
          alignItems: 'center',
          alignSelf: 'stretch',
          minHeight: '100%',
          py: '0',
          px: '0'
        },
        '& .MuiAutocomplete-tag': {
          my: '4px'
        }
      }}
    />

    {props.children}
  </StyledBar>
}
